import { combineLatest } from 'rxjs';
import { setUsingTab } from 'src/app/@utils/accessibility-helper';
import { BreakpointMinService } from 'src/app/@utils/breakpoint-min.service';
import { getFocusableElements } from 'src/app/@utils/get-focusable-elements.helper';

import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    NgZone,
    ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { ContentService } from '@core/services/content.service';
import { FilterService } from '@core/services/filter/filter.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { SearchService } from '@core/services/search.service';
import { SettingsService } from '@core/services/settings.service';
import { SiteStateService } from '@core/services/site-state/site-state.service';
import { customFilterParams } from '@features/product-filter/product-filter.config';
import { SearchInputComponent } from '@features/search/search-input/search-input.component';
import { SearchStateService } from '@features/search/search-state.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UrlHelperService } from '@core/services/url-helper.service';
import { HighlightPipe } from '@features/search/search-pipes/highlight.pipe';
import { GetQueryParamForLinkPipe } from '@features/search/search-pipes/get-query-param-for-link.pipe';
import { ShowSearchContentPipe } from '@features/search/search-pipes/show-search-content.pipe';
import { GetByContentTypePipe } from '@features/search/search-pipes/get-by-content-type.pipe';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { ProductMiniComponent } from '@features/product-mini/product-mini.component';
import { ArrowLinkComponent } from '@shared/arrow-link/arrow-link.component';
import { SearchContentResultsComponent } from '@features/search/search-content-results/search-content-results.component';
import { SetIsFocusedDirective } from '@shared/is-focused/is-focused.directive';
import { NgAfterViewInitDirective } from '@shared/ng-after-view-init/ng-after-view-init.directive';
import { SearchInputComponent as SearchInputComponent_1 } from '../../search-input/search-input.component';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { RedirectUrlSetup } from '@models/api-search.models';
import { toSignal } from '@angular/core/rxjs-interop';
import { mapToRelewiseVariation } from '@core/services/relewise/map-to-relewise-variation.helper';
import { distinctUntilChanged } from 'rxjs/operators';
import { MergeWithRetailMediaPipe } from '@features/search/search-pipes/merge-with-retail-media.pipe';

type AllowedKeys = 'Escape' | 'ArrowUp' | 'ArrowDown' | 'Tab';

@UntilDestroy()
@Component({
    selector: 'app-search-container',
    templateUrl: './search-container.component.html',
    styleUrls: ['./search-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        FormsModule,
        SearchInputComponent_1,
        NgAfterViewInitDirective,
        NgClass,
        NgFor,
        SetIsFocusedDirective,
        NgTemplateOutlet,
        SearchContentResultsComponent,
        ArrowLinkComponent,
        ProductMiniComponent,
        AsyncPipe,
        TranslateSafePipe,
        GetByContentTypePipe,
        ShowSearchContentPipe,
        GetQueryParamForLinkPipe,
        HighlightPipe,
        MergeWithRetailMediaPipe
    ]
})
export class SearchContainerComponent {
    private readonly breakpoints = toSignal(this.breakpointMinService.breakpoint$.pipe(distinctUntilChanged()));
    private readonly relewiseRetailMediaParams = computed(() => ({
        retailMediaPlacements: 'TOP' as const,
        retailMediaVariationKey: mapToRelewiseVariation(this.breakpoints())
    }));

    private changeInContentFlag = false;
    private redirects?: RedirectUrlSetup[];
    public elements: NodeListOf<HTMLElement>;

    @ViewChild('searchInputComponent')
    public searchInputComponent: SearchInputComponent;

    @ViewChild('searchContainer')
    public searchContainer: ElementRef<HTMLElement>;

    constructor(
        private readonly gtmService: GtmService,
        private readonly ngZone: NgZone,
        private readonly urlHelperService: UrlHelperService,
        public readonly filterService: FilterService,
        public readonly searchService: SearchService,
        public readonly searchStateService: SearchStateService,
        public readonly contentService: ContentService,
        public readonly siteStateService: SiteStateService,
        public readonly settingsService: SettingsService,
        public readonly router: Router,
        public readonly breakpointMinService: BreakpointMinService
    ) {
        combineLatest([this.searchStateService.contentSearchResults$, this.searchStateService.productSearchResults$])
            .pipe(untilDestroyed(this))
            .subscribe(([_, productsSearch]) => {
                const { redirectUrlsSetup } = productsSearch || {};
                this.redirects = redirectUrlsSetup;

                // Set a flag that provides info when using keyboard to navigate
                this.changeInContentFlag = true;
            });

        effect(() => {
            this.searchStateService.setSearchRelwiseRetailMediaParams(this.relewiseRetailMediaParams());
        });
    }

    // Dynamically set top position, because it differs from mobile and desktop
    // We need a specific top, otherwise it might cause visual glitches
    public setTop(dropdown: HTMLDivElement): void {
        // We eject from angular since we do not want to trigger change detection
        this.ngZone.runOutsideAngular(() => {
            if (this.searchContainer) {
                const searchContainerRect = this.searchContainer.nativeElement.getBoundingClientRect();
                dropdown.style.top = `${searchContainerRect.top + searchContainerRect.height}px`;
            }
        });
    }

    @HostListener('keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent): void {
        if (!this.searchInputComponent.searchInput.value) {
            return;
        }

        // Only if something has changed, do we do a new DOM query
        // 1. This makes sure we do not get content on every keydown event
        // 2. It also makes sure we do not get elements on every search update, if the keyboard isn't used in between anyways
        if (this.changeInContentFlag) {
            this.changeInContentFlag = false;
            this.elements = getFocusableElements(this.searchContainer.nativeElement);
        }

        const { key, target } = event;
        const { elements } = this;
        const firstElement = elements.item(0);
        const lastElement = elements.item(elements.length - 1);

        // This is instead of switch
        return {
            Escape: () => this.searchStateService.close(),
            ArrowUp: () => {
                if (target === firstElement) {
                    lastElement.focus();
                } else {
                    const index = Array.from(elements).findIndex(el => el === target);
                    elements.item(index - 1).focus();
                }
                setUsingTab(true);
            },
            ArrowDown: () => {
                if (target === lastElement) {
                    firstElement.focus();
                } else {
                    const index = Array.from(elements).findIndex(el => el === target);
                    elements.item(index + 1).focus();
                }
                setUsingTab(true);
            },
            Tab: () => {
                if (!event.shiftKey && target === lastElement) {
                    firstElement.focus();
                    event.preventDefault();
                } else if (event.shiftKey && target === firstElement) {
                    lastElement.focus();
                    event.preventDefault();
                }
                setUsingTab(true);
            }
        }[key as AllowedKeys]?.();
    }

    public setInputValueAndSubmit(searchPageUrl: string, valueToSet: string): void {
        this.searchInputComponent.searchInput.setValue(valueToSet, { emitEvent: false });
        this.handleSubmit(searchPageUrl, valueToSet);
    }

    public handleSubmit(searchPageUrl: string, searchInputValue: string): void {
        if (searchInputValue.length < 2) {
            return;
        }
        // Always close search overlay when submitting
        this.searchStateService.close();

        // Tracking
        this.gtmService.trackingEvents.trackSubmittedSearch(searchInputValue);

        // Navigate to the page, in case we have one URL on the redirects list
        if (this.redirects && this.redirects?.length >= 1) {
            const { pathname } = this.urlHelperService.getRelativeUrl(this.redirects[0].redirectUrl || '');
            if (pathname) {
                this.router.navigate([pathname]);
                return;
            }
        }

        const param = { [customFilterParams.searchQuery]: searchInputValue };
        if (this.router.url.split('?')[0] === searchPageUrl) {
            // Update queryParam with search input value
            this.filterService.reset(param);
            return;
        }
        // Navigate to search page (If not currently on)
        this.router.navigate([searchPageUrl], { queryParams: this.filterService.toFilterParams(param) });
    }

    public trackProductClick(searchInputValue: string, productName: string, index: number, hits: number): void {
        this.gtmService.trackingEvents.trackDropdownSearch(
            searchInputValue,
            productName,
            index.toString(),
            hits.toString()
        );
    }
}
